import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Phone, Users, Clock, FileText, Plus, Trash2, 
  CheckCircle, XCircle, UserPlus, Download, 
  BarChart3, Calendar, Loader2, AlertCircle,
  PhoneCall, UserCheck, Stethoscope, Pill
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CabinetManagement = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('queue');
  
  // Data states
  const [callQueue, setCallQueue] = useState([]);
  const [queueStats, setQueueStats] = useState({});
  const [staff, setStaff] = useState([]);
  const [waitingRoom, setWaitingRoom] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [dailyStats, setDailyStats] = useState({});
  const [weeklyStats, setWeeklyStats] = useState({});
  
  // Modal states
  const [showAddCall, setShowAddCall] = useState(false);
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [showPrescription, setShowPrescription] = useState(false);
  
  // Form states
  const [newCall, setNewCall] = useState({ patient_name: '', patient_phone: '', reason: '', priority: 'normal' });
  const [newStaff, setNewStaff] = useState({ first_name: '', last_name: '', role: 'secretary', email: '', phone: '' });
  const [newPrescription, setNewPrescription] = useState({ 
    patient_id: '', appointment_id: '', 
    items: [{ medication: '', dosage: '', frequency: '', duration: '', instructions: '' }] 
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [queueRes, queueStatsRes, staffRes, waitingRes, prescRes, dailyRes, weeklyRes] = await Promise.all([
        axios.get(`${API_URL}/api/cabinet/call-queue`, { headers }).catch(() => ({ data: { queue: [] } })),
        axios.get(`${API_URL}/api/cabinet/call-queue/stats`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/cabinet/staff`, { headers }).catch(() => ({ data: { staff: [] } })),
        axios.get(`${API_URL}/api/cabinet/waiting-room`, { headers }).catch(() => ({ data: { waiting_room: [] } })),
        axios.get(`${API_URL}/api/cabinet/prescriptions`, { headers }).catch(() => ({ data: { prescriptions: [] } })),
        axios.get(`${API_URL}/api/cabinet/stats/daily`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/cabinet/stats/weekly`, { headers }).catch(() => ({ data: {} }))
      ]);
      
      setCallQueue(queueRes.data.queue || []);
      setQueueStats(queueStatsRes.data || {});
      setStaff(staffRes.data.staff || []);
      setWaitingRoom(waitingRes.data.waiting_room || []);
      setPrescriptions(prescRes.data.prescriptions || []);
      setDailyStats(dailyRes.data || {});
      setWeeklyStats(weeklyRes.data || {});
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Call Queue handlers
  const handleAddCall = async () => {
    try {
      await axios.post(`${API_URL}/api/cabinet/call-queue`, newCall, { headers });
      toast.success('Appel ajouté à la file');
      setShowAddCall(false);
      setNewCall({ patient_name: '', patient_phone: '', reason: '', priority: 'normal' });
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleUpdateCallStatus = async (queueId, status) => {
    try {
      await axios.patch(`${API_URL}/api/cabinet/call-queue/${queueId}?status=${status}`, {}, { headers });
      toast.success('Statut mis à jour');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Staff handlers
  const handleAddStaff = async () => {
    try {
      await axios.post(`${API_URL}/api/cabinet/staff`, newStaff, { headers });
      toast.success('Personnel ajouté');
      setShowAddStaff(false);
      setNewStaff({ first_name: '', last_name: '', role: 'secretary', email: '', phone: '' });
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleRemoveStaff = async (staffId) => {
    if (!window.confirm('Supprimer ce membre du personnel ?')) return;
    try {
      await axios.delete(`${API_URL}/api/cabinet/staff/${staffId}`, { headers });
      toast.success('Personnel supprimé');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Waiting Room handlers
  const handleCallNextPatient = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/cabinet/waiting-room/call-next`, {}, { headers });
      if (res.data.success) {
        toast.success(`Patient appelé: ${res.data.patient?.patient_name || 'Suivant'}`);
        fetchAllData();
      } else {
        toast.info(res.data.message || 'Salle d\'attente vide');
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleCompleteConsultation = async (checkinId) => {
    try {
      await axios.post(`${API_URL}/api/cabinet/waiting-room/complete/${checkinId}`, {}, { headers });
      toast.success('Consultation terminée');
      fetchAllData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Prescription handlers
  const addPrescriptionItem = () => {
    setNewPrescription(prev => ({
      ...prev,
      items: [...prev.items, { medication: '', dosage: '', frequency: '', duration: '', instructions: '' }]
    }));
  };

  const updatePrescriptionItem = (index, field, value) => {
    setNewPrescription(prev => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  };

  const handleCreatePrescription = async () => {
    try {
      await axios.post(`${API_URL}/api/cabinet/prescriptions`, newPrescription, { headers });
      toast.success('Ordonnance créée');
      setShowPrescription(false);
      setNewPrescription({ 
        patient_id: '', appointment_id: '', 
        items: [{ medication: '', dosage: '', frequency: '', duration: '', instructions: '' }] 
      });
      fetchAllData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleDownloadPrescription = async (prescriptionId) => {
    try {
      const res = await axios.get(`${API_URL}/api/cabinet/prescriptions/${prescriptionId}/pdf`, {
        headers,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ordonnance_${prescriptionId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Téléchargement lancé');
    } catch (error) {
      toast.error('Erreur de téléchargement');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-700 border-red-300';
      case 'normal': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'low': return 'bg-gray-100 text-gray-700 border-gray-300';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getRoleLabel = (role) => {
    const roles = {
      secretary: 'Secrétaire',
      nurse: 'Infirmier(ère)',
      assistant: 'Assistant(e)'
    };
    return roles[role] || role;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800">
            🏥 Gestion du Cabinet
          </h1>
          <p className="text-slate-500 mt-1">
            Gérez votre pratique au quotidien
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Appels en attente</p>
                  <p className="text-2xl font-bold">{queueStats.pending_calls || 0}</p>
                </div>
                <PhoneCall className="w-8 h-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Salle d'attente</p>
                  <p className="text-2xl font-bold">{waitingRoom.length}</p>
                </div>
                <Users className="w-8 h-8 text-green-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">RDV aujourd'hui</p>
                  <p className="text-2xl font-bold">{dailyStats.total_appointments || 0}</p>
                </div>
                <Calendar className="w-8 h-8 text-purple-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm">Taux remplissage</p>
                  <p className="text-2xl font-bold">{dailyStats.fill_rate || 0}%</p>
                </div>
                <BarChart3 className="w-8 h-8 text-amber-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid grid-cols-5 gap-2 bg-white p-1 rounded-lg shadow-sm">
            <TabsTrigger value="queue" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white">
              <Phone className="w-4 h-4 mr-2" /> Appels
            </TabsTrigger>
            <TabsTrigger value="waiting" className="data-[state=active]:bg-green-500 data-[state=active]:text-white">
              <Clock className="w-4 h-4 mr-2" /> Salle d'attente
            </TabsTrigger>
            <TabsTrigger value="staff" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" /> Personnel
            </TabsTrigger>
            <TabsTrigger value="prescriptions" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
              <FileText className="w-4 h-4 mr-2" /> Ordonnances
            </TabsTrigger>
            <TabsTrigger value="stats" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white">
              <BarChart3 className="w-4 h-4 mr-2" /> Stats
            </TabsTrigger>
          </TabsList>

          {/* Call Queue Tab */}
          <TabsContent value="queue">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="w-5 h-5 text-blue-500" />
                    File d'attente téléphonique
                  </CardTitle>
                  <CardDescription>Gérez les appels entrants</CardDescription>
                </div>
                <Button onClick={() => setShowAddCall(true)} className="bg-blue-500 hover:bg-blue-600">
                  <Plus className="w-4 h-4 mr-2" /> Nouvel appel
                </Button>
              </CardHeader>
              <CardContent>
                {callQueue.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Phone className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Aucun appel en attente</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {callQueue.map((call, idx) => (
                      <div key={call.id} className={`p-4 rounded-lg border ${getPriorityColor(call.priority)}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-lg font-bold">#{call.position || idx + 1}</span>
                            <div>
                              <p className="font-semibold">{call.patient_name}</p>
                              <p className="text-sm opacity-75">{call.patient_phone}</p>
                              <p className="text-sm">{call.reason}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => handleUpdateCallStatus(call.id, 'in_progress')}>
                              <PhoneCall className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleUpdateCallStatus(call.id, 'completed')}>
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleUpdateCallStatus(call.id, 'cancelled')}>
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Waiting Room Tab */}
          <TabsContent value="waiting">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-green-500" />
                    Salle d'attente
                  </CardTitle>
                  <CardDescription>Patients présents au cabinet</CardDescription>
                </div>
                <Button onClick={handleCallNextPatient} className="bg-green-500 hover:bg-green-600">
                  <UserCheck className="w-4 h-4 mr-2" /> Appeler suivant
                </Button>
              </CardHeader>
              <CardContent>
                {waitingRoom.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Salle d'attente vide</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {waitingRoom.map((entry, idx) => (
                      <div key={entry.id} className="p-4 rounded-lg border border-green-200 bg-green-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center font-bold">
                              {entry.position || idx + 1}
                            </div>
                            <div>
                              <p className="font-semibold">{entry.patient_name || 'Patient'}</p>
                              <p className="text-sm text-green-600">
                                Arrivé à {new Date(entry.checked_in_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Badge variant={entry.status === 'in_consultation' ? 'default' : 'secondary'}>
                              {entry.status === 'in_consultation' ? 'En consultation' : 'En attente'}
                            </Badge>
                            {entry.status === 'in_consultation' && (
                              <Button size="sm" className="bg-green-600" onClick={() => handleCompleteConsultation(entry.id)}>
                                <CheckCircle className="w-4 h-4 mr-1" /> Terminer
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Staff Tab */}
          <TabsContent value="staff">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-purple-500" />
                    Personnel du cabinet
                  </CardTitle>
                  <CardDescription>Gérez votre équipe</CardDescription>
                </div>
                <Button onClick={() => setShowAddStaff(true)} className="bg-purple-500 hover:bg-purple-600">
                  <UserPlus className="w-4 h-4 mr-2" /> Ajouter
                </Button>
              </CardHeader>
              <CardContent>
                {staff.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Aucun personnel enregistré</p>
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2">
                    {staff.map((member) => (
                      <div key={member.id} className="p-4 rounded-lg border border-purple-200 bg-purple-50">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold">{member.first_name} {member.last_name}</p>
                            <Badge className="mt-1">{getRoleLabel(member.role)}</Badge>
                            {member.phone && <p className="text-sm text-slate-500 mt-1">{member.phone}</p>}
                            {member.email && <p className="text-sm text-slate-500">{member.email}</p>}
                          </div>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleRemoveStaff(member.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Prescriptions Tab */}
          <TabsContent value="prescriptions">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-amber-500" />
                    Ordonnances
                  </CardTitle>
                  <CardDescription>Gérez les prescriptions</CardDescription>
                </div>
                <Button onClick={() => setShowPrescription(true)} className="bg-amber-500 hover:bg-amber-600">
                  <Plus className="w-4 h-4 mr-2" /> Nouvelle ordonnance
                </Button>
              </CardHeader>
              <CardContent>
                {prescriptions.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Aucune ordonnance</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {prescriptions.map((presc) => (
                      <div key={presc.id} className="p-4 rounded-lg border border-amber-200 bg-amber-50">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold">{presc.prescription_number}</p>
                            <p className="text-sm text-slate-600">Patient: {presc.patient_name}</p>
                            <p className="text-sm text-slate-500">
                              {new Date(presc.created_at).toLocaleDateString('fr-FR')} - {presc.items?.length || 0} médicament(s)
                            </p>
                          </div>
                          <Button size="sm" variant="outline" onClick={() => handleDownloadPrescription(presc.id)}>
                            <Download className="w-4 h-4 mr-1" /> PDF
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Stats Tab */}
          <TabsContent value="stats">
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Statistiques du jour
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                      <span>Total RDV</span>
                      <span className="font-bold text-lg">{dailyStats.total_appointments || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                      <span>Complétés</span>
                      <span className="font-bold text-lg text-green-600">{dailyStats.completed || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                      <span>Absences</span>
                      <span className="font-bold text-lg text-red-600">{dailyStats.no_shows || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                      <span>Ordonnances</span>
                      <span className="font-bold text-lg text-amber-600">{dailyStats.prescriptions_issued || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Résumé hebdomadaire
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                      <span>Total RDV semaine</span>
                      <span className="font-bold text-lg text-blue-600">{weeklyStats.total_appointments || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                      <span>Complétés</span>
                      <span className="font-bold text-lg text-green-600">{weeklyStats.total_completed || 0}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                      <span>Taux remplissage moyen</span>
                      <span className="font-bold text-lg text-purple-600">{weeklyStats.average_fill_rate || 0}%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Add Call Modal */}
        <Dialog open={showAddCall} onOpenChange={setShowAddCall}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nouvel appel</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nom du patient</Label>
                <Input 
                  value={newCall.patient_name} 
                  onChange={(e) => setNewCall({ ...newCall, patient_name: e.target.value })}
                  placeholder="Jean Dupont"
                />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input 
                  value={newCall.patient_phone} 
                  onChange={(e) => setNewCall({ ...newCall, patient_phone: e.target.value })}
                  placeholder="+33 6 12 34 56 78"
                />
              </div>
              <div>
                <Label>Motif</Label>
                <Input 
                  value={newCall.reason} 
                  onChange={(e) => setNewCall({ ...newCall, reason: e.target.value })}
                  placeholder="Prise de RDV, Résultats, Question..."
                />
              </div>
              <div>
                <Label>Priorité</Label>
                <Select value={newCall.priority} onValueChange={(v) => setNewCall({ ...newCall, priority: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">🔴 Urgent</SelectItem>
                    <SelectItem value="normal">🔵 Normal</SelectItem>
                    <SelectItem value="low">⚪ Basse priorité</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddCall(false)}>Annuler</Button>
              <Button onClick={handleAddCall} className="bg-blue-500">Ajouter</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Staff Modal */}
        <Dialog open={showAddStaff} onOpenChange={setShowAddStaff}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ajouter un membre du personnel</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Prénom</Label>
                  <Input 
                    value={newStaff.first_name} 
                    onChange={(e) => setNewStaff({ ...newStaff, first_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Nom</Label>
                  <Input 
                    value={newStaff.last_name} 
                    onChange={(e) => setNewStaff({ ...newStaff, last_name: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Rôle</Label>
                <Select value={newStaff.role} onValueChange={(v) => setNewStaff({ ...newStaff, role: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="secretary">Secrétaire</SelectItem>
                    <SelectItem value="nurse">Infirmier(ère)</SelectItem>
                    <SelectItem value="assistant">Assistant(e)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={newStaff.email} 
                  onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })}
                />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input 
                  value={newStaff.phone} 
                  onChange={(e) => setNewStaff({ ...newStaff, phone: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddStaff(false)}>Annuler</Button>
              <Button onClick={handleAddStaff} className="bg-purple-500">Ajouter</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Prescription Modal */}
        <Dialog open={showPrescription} onOpenChange={setShowPrescription}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Nouvelle ordonnance</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>ID Patient</Label>
                  <Input 
                    value={newPrescription.patient_id} 
                    onChange={(e) => setNewPrescription({ ...newPrescription, patient_id: e.target.value })}
                    placeholder="ID du patient"
                  />
                </div>
                <div>
                  <Label>ID RDV (optionnel)</Label>
                  <Input 
                    value={newPrescription.appointment_id} 
                    onChange={(e) => setNewPrescription({ ...newPrescription, appointment_id: e.target.value })}
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-lg">Médicaments</Label>
                  <Button size="sm" variant="outline" onClick={addPrescriptionItem}>
                    <Plus className="w-4 h-4 mr-1" /> Ajouter
                  </Button>
                </div>
                
                {newPrescription.items.map((item, idx) => (
                  <div key={idx} className="p-3 border rounded-lg mb-3 bg-slate-50">
                    <div className="grid grid-cols-2 gap-3">
                      <Input 
                        placeholder="Médicament"
                        value={item.medication}
                        onChange={(e) => updatePrescriptionItem(idx, 'medication', e.target.value)}
                      />
                      <Input 
                        placeholder="Dosage (ex: 500mg)"
                        value={item.dosage}
                        onChange={(e) => updatePrescriptionItem(idx, 'dosage', e.target.value)}
                      />
                      <Input 
                        placeholder="Fréquence (ex: 3x/jour)"
                        value={item.frequency}
                        onChange={(e) => updatePrescriptionItem(idx, 'frequency', e.target.value)}
                      />
                      <Input 
                        placeholder="Durée (ex: 7 jours)"
                        value={item.duration}
                        onChange={(e) => updatePrescriptionItem(idx, 'duration', e.target.value)}
                      />
                    </div>
                    <Textarea 
                      className="mt-2"
                      placeholder="Instructions spéciales..."
                      value={item.instructions}
                      onChange={(e) => updatePrescriptionItem(idx, 'instructions', e.target.value)}
                    />
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPrescription(false)}>Annuler</Button>
              <Button onClick={handleCreatePrescription} className="bg-amber-500">Créer ordonnance</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CabinetManagement;
